import React, { useState, useEffect, useRef } from 'react';
import { ChatBot } from '../../components/ChatBot';
import { MultiFieldForm } from './fields/MultiFieldForm.component';
import { FormInputModes } from './FormInputModes';
import { getChatOptionsFromConfig } from '../../../shared';
import { PanelHeader } from '../../components/PanelHeader';
import { MessageSquareIcon, MinusIcon, RefreshCcw, XIcon } from 'lucide-react';
import styled from 'styled-components';
import { getFormInstance, useFormInstanceDispatch, useFormInstanceListener, useFormInstanceSelect } from './FormInstance.service';
import { useFluidForm } from './FormContext';
import { SelectListMessage } from './fields/SelectListMessage.component';
import { SimpleFormClearButton } from './SimpleFormClearButton';
import { ChatSummaryCard } from './ChatSummaryCard.component';
const headerHeight = 42;
const ErrorRetryBubble = styled.div `
  padding: 0.875rem;
  border-radius: 1rem;
  border: 1px solid #fca5a5;
  background: linear-gradient(to bottom right, #fff7f7, #fff1f1);
  font-size: 0.875rem;
  color: #374151;
`;
const RetryButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.625rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #ea580c;
  background: white;
  border: 1px solid #fed7aa;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: #fff7ed;
    border-color: #fb923c;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const MinimizeButton = styled.button.attrs({
    className: 'fluid-chat-minimize-button'
}) `
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;
const CloseButton = styled.button.attrs({
    className: 'fluid-chat-close-button'
}) `
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }
`;
const FloatingChatContainer = styled.div.attrs({
    className: 'fluid-floating-chat-container'
}) `
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: 0px;
  left: 0px;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  margin: 0;
  padding: 0;

  ${props => props.isMinimized ? `
    width: 280px;
    height: ${headerHeight}px; /* Just show the header when minimized */
  ` : `
    width: 360px;
    height: 500px;
  `}

  &.headless {
    position: static;
    bottom: auto;
    left: auto;
    box-shadow: none;
    background: transparent;
    border: none;
    border-radius: 0;
    width: 100% !important;
    height: 100% !important;
  }
`;
const NonFloatingContainer = styled.div `
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  &.headless {
    background: transparent;
    padding: 0;
    margin: 0;
  }
`;
const DEFAULT_INITIAL_MESSAGE = "";
export const FluidChat = ({ formKey, instanceId, onMessageReceived, onConversationCleared, initialMessage, autoSendMessage, chatPath, rounded = false, inputModes, rightActions, floating = false, onClose, title, subtitle, icon, headerStyle, inputFooterStyle, autoStartForm = false, getCurrentData, hideClearButton = false, ...props }) => {
    const isHeadless = props.headless === true;
    const headlessClass = isHeadless ? 'headless' : '';
    let enableMiniforms;
    let form = useFluidForm();
    let threadId = useFormInstanceSelect((state) => state.chatbotService.threadId, instanceId);
    let chatType = useFormInstanceSelect((state) => state.fluidForm?.getAdditionalConfigValue("styleConfig", "chatType"), instanceId);
    const isChatOnly = form.getAdditionalConfigValue("styleConfig", "layout") === "chat";
    initialMessage = getChatOptionsFromConfig(form)?.initialMessage || initialMessage || DEFAULT_INITIAL_MESSAGE;
    useFormInstanceSelect((state) => {
        state.chatbotService.setChatPath(chatPath);
    }, instanceId);
    useFormInstanceListener(instanceId, (event) => {
        if (event.type == "chatbotService.messageReceived") {
            onMessageReceived?.(event.value[0]);
        }
        if (event.type == "chatbotService.askFormQuestion") {
            setIsMinimized(false);
        }
    });
    useEffect(() => {
        if (!threadId) {
            return;
        }
        if (messages.length == 0 && (initialMessage || autoStartForm)) {
            dispatch('chatbotService.addInitialMessage', initialMessage, autoStartForm);
        }
        else if (messages.length > 0) {
            let lastMessageIsContinueCommand = getFormInstance(instanceId)?.chatbotService.lastMessageIsContinueCommand();
            let lastMessageIsFromBot = getFormInstance(instanceId)?.chatbotService.lastMessageIsFromBot();
            if (lastMessageIsContinueCommand || lastMessageIsFromBot) {
                return;
            }
            dispatch('chatbotService.continueFillingCommand');
        }
    }, [threadId]);
    useEffect(() => {
        getFormInstance(instanceId)?.chatbotService.setChatPath(chatPath);
        return () => {
        };
    }, []);
    const isLoading = useFormInstanceSelect((state) => state.chatbotService.isLoading || state.isSubmitting, instanceId);
    const dispatch = useFormInstanceDispatch(instanceId);
    const [isMinimized, setIsMinimized] = useState(false);
    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
    };
    const [isCollapsed, setIsCollapsed] = useState(props.collapsed);
    useEffect(() => {
        setIsCollapsed(props.collapsed);
    }, [props.collapsed]);
    const handleClose = () => {
        onClose?.();
    };
    if (props.enableMiniforms !== undefined) {
        enableMiniforms = props.enableMiniforms;
    }
    else if (chatType == "microforms") {
        enableMiniforms = true;
    }
    else if (chatType == "conversational") {
        enableMiniforms = false;
    }
    let chatHistory = useFormInstanceSelect((state) => state.chatbotService.getMessages(), instanceId);
    const autoSentRef = useRef(false);
    const hasAnyUserMessage = chatHistory?.some((m) => m?.role === 'user');
    let messages = convertToCustomMessages(chatHistory, enableMiniforms);
    function convertToCustomMessages(chatHistory, enableMiniforms) {
        return chatHistory.map((msg, index) => {
            let asBotMessage = msg;
            const baseMessage = {
                role: msg.role,
                content: msg.text,
                messageType: asBotMessage.messageType,
                data: msg.data,
                messageFields: asBotMessage.messageFields,
                messageData: asBotMessage?.messageData,
            };
            if (asBotMessage.messageType === 'error' && index === chatHistory.length - 1) {
                baseMessage.content = '';
                baseMessage.customComponent = (React.createElement(ErrorRetryBubble, null,
                    React.createElement("div", { dangerouslySetInnerHTML: { __html: msg.text } }),
                    React.createElement(RetryButton, { onClick: () => handleRetry(), disabled: isLoading },
                        React.createElement(RefreshCcw, { size: 12 }),
                        "Try again")));
                return baseMessage;
            }
            if (isChatOnly && asBotMessage.messageType === 'success' && index === chatHistory.length - 1) {
                baseMessage.content = '';
                baseMessage.customComponent = (React.createElement(ChatSummaryCard, { message: msg.text, onSubmit: () => dispatch('submit'), isSubmitting: isLoading }));
                return baseMessage;
            }
            // If the fields contain a "scheduling" field, then we need to render a scheduling form
            const includesScheduling = asBotMessage.messageFields?.every(field => form.getConfig(field)?.type === "scheduling");
            if (!enableMiniforms && asBotMessage.messageType === 'question' &&
                (asBotMessage.messageFields.length === 1 && includesScheduling &&
                    form.getConfig(asBotMessage.messageFields[0])?.schedulingConfig?.variant === "natural-language") &&
                asBotMessage.messageData?.selectOptions && asBotMessage.messageData?.selectOptions.length > 0 &&
                asBotMessage.messageFields && asBotMessage.messageFields.length === 1) {
                baseMessage.customComponent = (React.createElement(SelectListMessage, { readOnly: index !== chatHistory.length - 1, field: asBotMessage.messageFields?.[0], options: asBotMessage.messageData.selectOptions, onSelect: (selectedLabel) => handleSelectListMessageSubmit(selectedLabel), isLoading: isLoading }));
            }
            if (includesScheduling) {
                enableMiniforms = true;
            }
            if (!enableMiniforms) {
                return baseMessage;
            }
            // Check if this is a multifield question that should render as a form
            if (index == chatHistory.length - 1 && asBotMessage.messageType === 'question' &&
                asBotMessage.messageFields &&
                asBotMessage.messageFields?.length > 0) {
                let validFields = asBotMessage.messageFields.every(field => form.getConfig(field));
                //TODO: Not rendering the form if the fields are not valid, this ia defensive approach as the agent can allucinate, this should happen in the backend
                if (!validFields) {
                    return baseMessage;
                }
                baseMessage.customComponent = (React.createElement(MultiFieldForm, { fields: asBotMessage.messageFields, data: msg.data, onSubmit: (formData) => handleMultiFieldSubmit(formData), isLoading: isLoading }));
            }
            return baseMessage;
        });
    }
    ;
    const handleMessage = (message) => {
        if (message.role === 'user') {
            let currentData = getCurrentData?.() || form.getFormSubmission();
            dispatch('chatbotService.sendChatMessage', { text: message.content, currentData });
        }
    };
    // One-shot auto-send of an initial user message (e.g. prompt init flow).
    useEffect(() => {
        if (!threadId)
            return;
        if (!autoSendMessage)
            return;
        if (autoSentRef.current)
            return;
        if (hasAnyUserMessage)
            return;
        autoSentRef.current = true;
        const currentData = getCurrentData?.() || form.getFormSubmission();
        dispatch('chatbotService.sendChatMessage', { text: autoSendMessage, currentData });
    }, [threadId, autoSendMessage, hasAnyUserMessage]);
    const handleMultiFieldSubmit = async (formData) => {
        // Format the form data as requested: fieldName: value
        const formattedMessage = Object.entries(formData)
            .map(([fieldLabel, value]) => `${fieldLabel}: ${value}`)
            .join('\n');
        let currentData = getCurrentData?.() || form.getFormSubmission();
        dispatch('chatbotService.sendChatMessage', { text: formattedMessage, currentData });
    };
    const handleSelectListMessageSubmit = async (selectedLabel) => {
        let currentData = getCurrentData?.() || form.getFormSubmission();
        dispatch('chatbotService.sendChatMessage', { text: selectedLabel, currentData });
    };
    const handleRetry = () => {
        const currentData = getCurrentData?.() || form.getFormSubmission();
        dispatch('chatbotService.retryLastMessage', currentData);
    };
    const Header = isHeadless ? null : (React.createElement(PanelHeader, { title: title || `Form AI`, subtitle: subtitle, icon: icon || MessageSquareIcon, topRounded: rounded, style: headerStyle, actions: React.createElement("div", { className: "flex items-center gap-2" },
            rightActions,
            floating && (React.createElement(React.Fragment, null,
                React.createElement(MinimizeButton, { onClick: toggleMinimize, title: isMinimized ? "Expand chat" : "Minimize chat" }, isMinimized ? (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" },
                    React.createElement("polyline", { points: "6 9 12 15 18 9" }))) : (React.createElement(MinusIcon, { size: 16 }))),
                React.createElement(CloseButton, { onClick: handleClose, title: "Close chat" },
                    React.createElement(XIcon, { size: 16 }))))) }));
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { header: Header, floating: floating, isMinimized: isMinimized, headlessClass: headlessClass },
            React.createElement("div", { style: {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    overflow: 'hidden',
                } },
                React.createElement("div", { className: isCollapsed ? 'hidden' : 'h-full overflow-hidden' },
                    React.createElement(ChatBot, { messages: messages, onMessage: handleMessage, isLoading: isLoading, rounded: rounded, roundedTextArea: !props.headless, inputFooterStyle: inputFooterStyle, footer: React.createElement("div", { className: "flex items-center justify-between" },
                            React.createElement(FormInputModes, { layout: "horizontal", alignTooltips: "left", spacing: "md", inputModes: inputModes, getCurrentData: getCurrentData }),
                            !hideClearButton && React.createElement(SimpleFormClearButton, null)) })),
                isCollapsed && (React.createElement("div", { className: "p-2 flex flex-row items-center w-full" },
                    React.createElement(FormInputModes, { layout: "horizontal", alignTooltips: "left", spacing: "md", inputModes: inputModes, getCurrentData: getCurrentData })))))));
};
function Container({ header, children, floating, isMinimized, headlessClass }) {
    if (floating) {
        return (React.createElement(FloatingChatContainer, { isMinimized: isMinimized, className: `f-bg ${headlessClass}` },
            header,
            children));
    }
    // For non-floating chat, use full height to enable proper scrolling
    return (React.createElement(NonFloatingContainer, { className: `f-bg ${headlessClass}` },
        header,
        children));
}
